import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { Users, Save, RefreshCw, Gift, Percent, Award, TrendingUp } from 'lucide-react';

const AdminReferralSettingsPage = () => {
    const [settings, setSettings] = useState({
        // Trading Fee (%)
        tradingFee: 0.1,

        // 3-Level Referral Commission (% of trading fee)
        level1Commission: 30,
        level2Commission: 20,
        level3Commission: 10,

        // Signup Bonus
        signupBonusEnabled: true,
        signupBonusAmount: 50,
        signupBonusCurrency: 'INR',

        // Minimum requirements
        minTradeForReferral: 100,
        maxDailyCommission: 10000,

        // Bonus conditions
        referralRequirements: {
            minKYCRequired: false,
            minTrades: 1,
            minVolume: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState({ totalReferrals: 0, totalCommissionPaid: 0, activeReferrers: 0 });

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/settings/referral');
            if (res.data.referralSettings) setSettings(res.data.referralSettings);
            if (res.data.stats) setStats(res.data.stats);
        } catch (error) {
            console.error('Failed to load referral settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.put('/admin/settings/referral', { referralSettings: settings });
            toast.success('Referral settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="min-h-screen bg-slate-900"><AdminNavbar /><div className="text-center py-12 text-gray-400">Loading...</div></div>;

    const totalCommission = settings.level1Commission + settings.level2Commission + settings.level3Commission;

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="text-purple-400" /> Referral Settings</h1>
                        <p className="text-gray-400 text-sm">Configure referral commissions and bonuses</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchSettings} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50">
                            <Save size={18} /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-5xl">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                        <p className="text-gray-400 text-sm">Total Referrals</p>
                        <p className="text-2xl font-bold text-white">{stats.totalReferrals}</p>
                    </div>
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                        <p className="text-gray-400 text-sm">Commission Paid</p>
                        <p className="text-2xl font-bold text-green-400">₹{stats.totalCommissionPaid.toLocaleString()}</p>
                    </div>
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                        <p className="text-gray-400 text-sm">Active Referrers</p>
                        <p className="text-2xl font-bold text-purple-400">{stats.activeReferrers}</p>
                    </div>
                </div>

                <div className="grid gap-6">
                    {/* Trading Fee */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Percent className="text-yellow-400" /> Trading Fee</h2>
                        <div className="max-w-xs">
                            <label className="text-gray-400 text-sm">Trading Fee (%)</label>
                            <input type="number" step="0.01" min="0" max="10" value={settings.tradingFee} onChange={e => setSettings({ ...settings, tradingFee: parseFloat(e.target.value) })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-lg" />
                            <p className="text-gray-500 text-xs mt-1">Fee charged on each trade (both maker & taker)</p>
                        </div>
                    </div>

                    {/* 3-Level Commission */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><TrendingUp className="text-green-400" /> 3-Level Referral Commission</h2>
                        <p className="text-gray-400 text-sm mb-4">Percentage of trading fee given to referrers at each level</p>

                        <div className="grid grid-cols-3 gap-6">
                            <div className="bg-slate-700/50 rounded-lg p-4 border-2 border-green-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <Award className="text-green-400" />
                                    <span className="text-green-400 font-bold">Level 1</span>
                                </div>
                                <label className="text-gray-400 text-xs">Direct Referral</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input type="number" min="0" max="100" value={settings.level1Commission} onChange={e => setSettings({ ...settings, level1Commission: parseFloat(e.target.value) })} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-lg" />
                                    <span className="text-gray-400">%</span>
                                </div>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg p-4 border-2 border-blue-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <Award className="text-blue-400" />
                                    <span className="text-blue-400 font-bold">Level 2</span>
                                </div>
                                <label className="text-gray-400 text-xs">2nd Level</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input type="number" min="0" max="100" value={settings.level2Commission} onChange={e => setSettings({ ...settings, level2Commission: parseFloat(e.target.value) })} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-lg" />
                                    <span className="text-gray-400">%</span>
                                </div>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg p-4 border-2 border-purple-500/30">
                                <div className="flex items-center gap-2 mb-2">
                                    <Award className="text-purple-400" />
                                    <span className="text-purple-400 font-bold">Level 3</span>
                                </div>
                                <label className="text-gray-400 text-xs">3rd Level</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input type="number" min="0" max="100" value={settings.level3Commission} onChange={e => setSettings({ ...settings, level3Commission: parseFloat(e.target.value) })} className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-white text-lg" />
                                    <span className="text-gray-400">%</span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                            <p className="text-gray-400 text-sm">
                                <strong>Example:</strong> If trading fee is <span className="text-yellow-400">{settings.tradingFee}%</span> and user trades ₹10,000:
                            </p>
                            <p className="text-gray-300 text-sm mt-1">
                                Fee = ₹{(10000 * settings.tradingFee / 100).toFixed(2)} →
                                L1 gets ₹{((10000 * settings.tradingFee / 100) * settings.level1Commission / 100).toFixed(2)},
                                L2 gets ₹{((10000 * settings.tradingFee / 100) * settings.level2Commission / 100).toFixed(2)},
                                L3 gets ₹{((10000 * settings.tradingFee / 100) * settings.level3Commission / 100).toFixed(2)}
                            </p>
                            <p className="text-yellow-400 text-xs mt-2">Total commission: {totalCommission}% of trading fee</p>
                        </div>
                    </div>

                    {/* Signup Bonus */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Gift className="text-pink-400" /> Signup Bonus</h2>

                        <div className="space-y-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={settings.signupBonusEnabled} onChange={e => setSettings({ ...settings, signupBonusEnabled: e.target.checked })} className="w-5 h-5 rounded" />
                                <span className="text-gray-300">Enable signup bonus for referred users</span>
                            </label>

                            {settings.signupBonusEnabled && (
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="text-gray-400 text-sm">Bonus Amount</label>
                                        <div className="flex gap-2 mt-1">
                                            <input type="number" min="0" value={settings.signupBonusAmount} onChange={e => setSettings({ ...settings, signupBonusAmount: parseFloat(e.target.value) })} className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                                            <select value={settings.signupBonusCurrency} onChange={e => setSettings({ ...settings, signupBonusCurrency: e.target.value })} className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                                                <option value="INR">INR</option>
                                                <option value="USDT">USDT</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Limits */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                        <h2 className="text-lg font-bold text-white mb-4">Limits & Requirements</h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-gray-400 text-sm">Min Trade for Referral Commission (₹)</label>
                                <input type="number" min="0" value={settings.minTradeForReferral} onChange={e => setSettings({ ...settings, minTradeForReferral: parseFloat(e.target.value) })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-sm">Max Daily Commission (₹)</label>
                                <input type="number" min="0" value={settings.maxDailyCommission} onChange={e => setSettings({ ...settings, maxDailyCommission: parseFloat(e.target.value) })} className="w-full mt-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminReferralSettingsPage;
